/**
 * Label  component
 *
 * @package ext-components-base
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";

import {
	Type,
	defineElementaryComponent,
	createSubScope,
	BP,
	SCHEMA_VALUE_TYPE
} from "@adapptio/lib-blueprint";

import {
	TViewComponentDefinition,
	TComponentReact,
	Label,
	getValuesFromStringEnum,
	termsEditor as LibComponentsTerms,
	VERTICAL_ALIGN,
	VERTICAL_ALIGN_default,
	ClassList,
	TextSchema,
	TextValueSchema,
	IconSchema,
	getStringEnumValue,
	IconSourceSchema,
	HORIZONTAL_ALIGN,
	HORIZONTAL_ALIGN_default,
	BackgroundColorSchema,
	StyleSheet,
	propGroups,
	IconProps,
	TextProps,
	LABEL_FLOW,
	LABEL_FLOW_default,
	LABEL_FLOW_string,
	HORIZONTAL_ALIGN_opts,
	VERTICAL_ALIGN_opts,
	useStyleSheetRegistry,
	isEventEnabled,
	BorderRadiusSchema,
	LinkSchema
} from "@adapptio/lib-components";
import { BaseEvents } from "../../base/BaseEvents";
import { termsEditor } from "../../terms";

interface ComponentLabel_State {}

const ComponentLabel_Props = {
	text: BP.Prop(
		TextSchema({
			alias: "text_label",
			props: {
				value: BP.Prop(
					TextValueSchema({
						alias: "textValue_label",
						default: "Label"
					}),
					TextProps.value.order,
					TextProps.value.group
				),

				align: BP.Prop(BP.Void({})),

				overflow: BP.Prop(BP.Void({}))
			}
		}),
		0,
		propGroups.common
	),

	icon: BP.Prop(
		IconSchema({
			alias: "icon_label",
			props: {
				source: BP.Prop(
					IconSourceSchema({
						alias: "iconSource_label",
						default: "mdi/check"
					}),
					IconProps.source.order,
					IconProps.source.group
				)
			}
		}),
		10,
		propGroups.common
	),

	flow: BP.Prop(
		BP.Enum.String({
			...LibComponentsTerms.schemas.label.flow,
			options: getValuesFromStringEnum(LABEL_FLOW, LibComponentsTerms.schemas.label.flowValues),
			default: LABEL_FLOW_default
		}),
		20,
		propGroups.common
	),

	horizontalAlign: BP.Prop(
		BP.Enum.String({
			...LibComponentsTerms.schemas.common.horizontalAlign,
			options: getValuesFromStringEnum(
				HORIZONTAL_ALIGN,
				LibComponentsTerms.schemas.common.horizontalAlignValues,
				HORIZONTAL_ALIGN_opts
			),
			default: HORIZONTAL_ALIGN_default,
			editorOptions: {
				controlType: "buttons"
			}
		}),
		30,
		propGroups.common
	),

	verticalAlign: BP.Prop(
		BP.Enum.String({
			...LibComponentsTerms.schemas.common.verticalAlign,
			options: getValuesFromStringEnum(
				VERTICAL_ALIGN,
				LibComponentsTerms.schemas.common.verticalAlignValues,
				VERTICAL_ALIGN_opts
			),
			default: VERTICAL_ALIGN_default,
			editorOptions: {
				controlType: "buttons"
			}
		}),
		40,
		propGroups.common
	),

	tooltip: BP.Prop(
		BP.String({
			...LibComponentsTerms.schemas.label.tooltip,
		}),
		50,
		propGroups.common
	),

	link: BP.Prop(LinkSchema(), 50, propGroups.link),

	backgroundColor: BP.Prop(BackgroundColorSchema(), 0, propGroups.style),

	borderRadius: BP.Prop(BorderRadiusSchema(), 10, propGroups.style)
};

const ComponentLabel_Events = {
	...BaseEvents
};

const ComponentLabel_Definition = defineElementaryComponent<
	typeof ComponentLabel_Props,
	ComponentLabel_State,
	typeof ComponentLabel_Events
>({
	...termsEditor.components.label.component,

	name: "label",

	category: "content",

	icon: "mdi/text-short",

	docUrl: "...",

	order: 20,

	props: ComponentLabel_Props,

	events: ComponentLabel_Events,

	resolve: (spec, state) => {
		return state || {};
	},

	getScopeData: (spec, state) => {
		return {};
	},

	getScopeType: () => {
		return Type.Object({
			props: {}
		});
	}
});

const ComponentLabel_React: TComponentReact<typeof ComponentLabel_Definition> = ({
	props,
	componentInstance,
	reactComponentClassList
}) => {
	const { backgroundColor, borderRadius } = props;

	const elementProps: React.HTMLProps<HTMLDivElement> = {};

	const { classList, idClassName } = ClassList.getElementClassListAndIdClassName(
		"cmp-label",
		componentInstance.safePath,
		{ componentClassList: reactComponentClassList }
	);

	const styleSheetRegistry = useStyleSheetRegistry();

	const styleSheet = React.useMemo(() => {
		const result = new StyleSheet();

		// Background color

		if (backgroundColor) {
			result.addColorProperties({
				selector: `.${idClassName}`,
				name: "element-background-color",
				value: backgroundColor
			});
		}

		// Border radius

		if (borderRadius) {
			result.addString(`.${idClassName}`, `--element-border-radius: ${borderRadius} !important;`);
		}

		return result;
	}, [ idClassName, backgroundColor, borderRadius ]);

	styleSheetRegistry.add(idClassName, styleSheet);

	const horizontalAlignValue = getStringEnumValue(
		HORIZONTAL_ALIGN,
		props.horizontalAlign,
		HORIZONTAL_ALIGN_default
	);
	const verticalAlignValue = getStringEnumValue(
		VERTICAL_ALIGN,
		props.verticalAlign,
		VERTICAL_ALIGN_default
	);

	classList.addModifiers({
		"horizontal-align": horizontalAlignValue,
		"vertical-align": verticalAlignValue
	});

	// Editable

	const textProp = componentInstance.modelNode?.props?.props?.text as any;
	const textPropsModelNode = textProp?.type === SCHEMA_VALUE_TYPE.CONST ? textProp.constant : null;

	// Event handler

	const _clickHandler = React.useMemo(() => {
		if (isEventEnabled(componentInstance.eventEnabled.click, componentInstance.componentMode)) {
			return () => componentInstance.eventTriggers.click((parentScope) => createSubScope(parentScope));
		}

		return;
	}, [
		componentInstance.componentMode,
		componentInstance.eventEnabled.click,
		componentInstance.eventTriggers.click
	]);

	if (_clickHandler) {
		classList.add("cmp-label--event-click");

		elementProps.tabIndex = 0;
		elementProps.onClick = _clickHandler;
	}

	return (
		<div {...elementProps} className={classList.toClassName()}>
			<Label
				text={textPropsModelNode ? { ...props.text, propsModelNode: textPropsModelNode } : props.text}
				icon={props.icon}
				flow={props.flow as LABEL_FLOW_string}
				tooltip={props.tooltip}
				link={props.link}
				componentPath={[ ...componentInstance.safePath, "label" ]}
				componentMode={componentInstance.componentMode}
			/>
		</div>
	);
};

export const ComponentLabel: TViewComponentDefinition<typeof ComponentLabel_Definition> = {
	...ComponentLabel_Definition,
	reactComponent: ComponentLabel_React
};
